import React, { useRef, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { X, Send, Trash2, Sparkles, Loader2, Check } from 'lucide-react';

// Segment Tag Input Component with Autocomplete
const SegmentTagInput = ({ tags, availableTags, onAddTag, onRemoveTag }) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  
  // Filter suggestions based on input and exclude already selected tags
  const suggestions = inputValue.trim() 
    ? availableTags.filter(tag => 
        tag.toLowerCase().includes(inputValue.toLowerCase()) && 
        !tags.includes(tag)
      )
    : availableTags.filter(tag => !tags.includes(tag));
  
  const handleAddTag = (tag) => {
    onAddTag(tag);
    setInputValue('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      handleAddTag(inputValue.trim());
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };
  
  return (
    <div>
      <Label>User Segment Tags</Label>
      <div className="mt-2 space-y-2">
        {/* Display existing tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, idx) => (
              <span 
                key={idx}
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 rounded-full text-sm"
              >
                {tag}
                <button
                  onClick={() => onRemoveTag(idx)}
                  className="hover:text-blue-600 dark:hover:text-blue-300"
                  type="button"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        
        {/* Add new tag input with autocomplete */}
        <div className="relative">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onKeyDown={handleKeyDown}
              placeholder="Type to search or add new tag..."
              className="flex-1"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => inputValue.trim() && handleAddTag(inputValue.trim())}
              disabled={!inputValue.trim()}
              type="button"
            >
              Add
            </Button>
          </div>
          
          {/* Autocomplete dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-40 overflow-y-auto">
              <div className="p-1 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                Existing tags (click to add)
              </div>
              {suggestions.slice(0, 8).map((tag, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-700 dark:text-gray-300"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleAddTag(tag);
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>
        
        <p className="text-xs text-gray-500">
          {availableTags.length > 0 
            ? 'Type to search existing tags or create new ones' 
            : 'Press Enter or click Add to create tags'}
        </p>
      </div>
    </div>
  );
};

const EditModal = ({
  isOpen,
  type,
  index,
  formData,
  setFormData,
  onSave,
  onDelete,
  onClose,
  copilotMessages,
  copilotInput,
  setCopilotInput,
  copilotLoading,
  onCopilotSend,
  onApplyUpdate,
  deleteConfirm,
  setDeleteConfirm,
  availableTags = []
}) => {
  const chatEndRef = useRef(null);
  
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [copilotMessages]);
  
  if (!isOpen) return null;
  
  const isNewItem = index === null;
  const canDelete = !isNewItem && type !== 'vision';
  
  const getTitle = () => {
    const action = isNewItem ? 'Add New' : 'Edit';
    const typeLabel = {
      vision: 'High-Level Vision',
      epic: 'Epic',
      persona: 'Persona',
      goal: 'Goal'
    }[type];
    return `${action} ${typeLabel}`;
  };
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const updateRequirement = (idx, value) => {
    const newReqs = [...(formData.requirements || [])];
    newReqs[idx] = value;
    setFormData(prev => ({ ...prev, requirements: newReqs }));
  };
  
  const addRequirement = () => {
    setFormData(prev => ({ 
      ...prev, 
      requirements: [...(prev.requirements || []), ''] 
    }));
  };
  
  const removeRequirement = (idx) => {
    const current = formData.requirements || [];
    setFormData(prev => ({ 
      ...prev, 
      requirements: current.filter((_, i) => i !== idx) 
    }));
  };
  
  const updateMetricItem = (idx, value) => {
    const newMetrics = [...(formData.metricsList || [])];
    newMetrics[idx] = value;
    setFormData(prev => ({ ...prev, metricsList: newMetrics }));
  };
  
  const addMetricItem = () => {
    setFormData(prev => ({ 
      ...prev, 
      metricsList: [...(prev.metricsList || []), ''] 
    }));
  };
  
  const removeMetricItem = (idx) => {
    setFormData(prev => ({ 
      ...prev, 
      metricsList: prev.metricsList.filter((_, i) => i !== idx) 
    }));
  };
  
  // Segment tag management
  const addSegmentTag = (tag) => {
    if (!tag.trim()) return;
    const currentTags = formData.segment_tags || [];
    if (!currentTags.includes(tag.trim())) {
      setFormData(prev => ({ 
        ...prev, 
        segment_tags: [...currentTags, tag.trim()]
      }));
    }
  };
  
  const removeSegmentTag = (idx) => {
    setFormData(prev => ({ 
      ...prev, 
      segment_tags: (prev.segment_tags || []).filter((_, i) => i !== idx)
    }));
  };
  
  // Render form fields based on type
  const renderForm = () => {
    switch (type) {
      case 'vision':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="vision">Vision Statement</Label>
              <Textarea
                id="vision"
                value={formData.vision || ''}
                onChange={(e) => updateField('vision', e.target.value)}
                rows={12}
                className="mt-2"
                placeholder="Describe your product vision..."
              />
            </div>
          </div>
        );
        
      case 'epic':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Epic Name *</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                className="mt-2"
                placeholder="e.g., User Authentication System"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                rows={5}
                className="mt-2"
                placeholder="What is this epic about?"
              />
            </div>
            <div>
              <Label>Features</Label>
              <div className="mt-2 space-y-2">
                {(formData.requirements || []).map((req, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={req}
                      onChange={(e) => updateRequirement(idx, e.target.value)}
                      placeholder="Feature..."
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRequirement(idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addRequirement}>
                  + Add Feature
                </Button>
              </div>
            </div>
          </div>
        );
        
      case 'persona':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name || ''}
                  onChange={(e) => updateField('name', e.target.value)}
                  className="mt-2"
                  placeholder="e.g., Sarah the Product Lead"
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  value={formData.role || ''}
                  onChange={(e) => updateField('role', e.target.value)}
                  className="mt-2"
                  placeholder="e.g., Senior Product Manager"
                />
              </div>
            </div>
            <SegmentTagInput 
              tags={formData.segment_tags || []}
              availableTags={availableTags}
              onAddTag={addSegmentTag}
              onRemoveTag={removeSegmentTag}
            />
            <div>
              <Label htmlFor="goals">Goals</Label>
              <Textarea
                id="goals"
                value={formData.goals || ''}
                onChange={(e) => updateField('goals', e.target.value)}
                rows={4}
                className="mt-2"
                placeholder="What do they want to achieve?"
              />
            </div>
            <div>
              <Label htmlFor="pain_points">Pain Points</Label>
              <Textarea
                id="pain_points"
                value={formData.pain_points || ''}
                onChange={(e) => updateField('pain_points', e.target.value)}
                rows={4}
                className="mt-2"
                placeholder="What frustrations do they have?"
              />
            </div>
            <div>
              <Label htmlFor="context">Context</Label>
              <Textarea
                id="context"
                value={formData.context || ''}
                onChange={(e) => updateField('context', e.target.value)}
                rows={4}
                className="mt-2"
                placeholder="Background, work environment, etc."
              />
            </div>
          </div>
        );
        
      case 'goal':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Goal Name *</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                className="mt-2"
                placeholder="e.g., Increase User Retention"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                rows={4}
                className="mt-2"
                placeholder="What does this metric category measure?"
              />
            </div>
            <div>
              <Label>Individual Metrics</Label>
              <div className="mt-2 space-y-2">
                {(formData.metricsList || []).map((metric, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={metric}
                      onChange={(e) => updateMetricItem(idx, e.target.value)}
                      placeholder="e.g., Monthly Active Users > 10K"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMetricItem(idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addMetricItem}>
                  + Add Metric
                </Button>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      
      {/* Modal */}
      <div className="fixed inset-4 md:inset-8 lg:inset-12 bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {getTitle()}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Content - 60/40 split */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left side - Form (60%) */}
          <div className="w-[60%] p-6 overflow-y-auto border-r border-gray-200 dark:border-gray-700">
            {renderForm()}
          </div>
          
          {/* Right side - AI Co-Pilot (40%) */}
          <div className="w-[40%] flex flex-col bg-gray-50 dark:bg-gray-900">
            {/* Co-Pilot Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">AI Co-Pilot</h3>
                  <p className="text-xs text-gray-500">Ask for help or suggestions</p>
                </div>
              </div>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {copilotMessages.map((msg, i) => {
                // Parse message for update blocks and display content
                let displayContent = msg.content;
                let updateData = msg.proposedUpdate;
                
                // ALWAYS try to clean JSON from display content for assistant messages
                if (msg.role === 'assistant') {
                  // First, strip any JSON from displayContent regardless of whether updateData exists
                  if (displayContent.includes('{') && displayContent.includes('"')) {
                    const jsonStart = displayContent.indexOf('{');
                    if (jsonStart !== -1) {
                      let braceCount = 0;
                      let jsonEnd = jsonStart;
                      let inString = false;
                      let prevChar = '';
                      
                      for (let i = jsonStart; i < displayContent.length; i++) {
                        const char = displayContent[i];
                        if (char === '"' && prevChar !== '\\') inString = !inString;
                        if (!inString) {
                          if (char === '{') braceCount++;
                          if (char === '}') braceCount--;
                        }
                        prevChar = char;
                        if (braceCount === 0 && i > jsonStart) {
                          jsonEnd = i + 1;
                          break;
                        }
                      }
                      
                      if (jsonEnd > jsonStart) {
                        const jsonStr = displayContent.substring(jsonStart, jsonEnd);
                        // Check if it looks like a valid JSON object with expected keys
                        if (jsonStr.includes('"name"') || jsonStr.includes('"description"') || jsonStr.includes('"requirements"') || jsonStr.includes('"capabilities"')) {
                          // Extract JSON for updateData if not already set
                          if (!updateData) {
                            try {
                              updateData = JSON.parse(jsonStr);
                            } catch (e) {
                              // Ignore parse errors
                            }
                          }
                          // Always remove JSON from display content
                          displayContent = displayContent.substring(0, jsonStart).trim();
                          // Clean up common phrases
                          displayContent = displayContent
                            .replace(/Here's how the revised.*?:\s*$/i, '')
                            .replace(/Here's the revised.*?:\s*$/i, '')
                            .replace(/Here are the.*?:\s*$/i, '')
                            .replace(/:\s*$/, '')
                            .trim();
                          if (!displayContent) {
                            displayContent = "I've prepared the changes for you.";
                          }
                        }
                      }
                    }
                  }
                }
                
                // Check for JSON code blocks in assistant messages (supports ```update, ```json, or plain ```)
                if (msg.role === 'assistant' && !updateData) {
                  // Try to match ```update```, ```json```, or plain ``` blocks containing JSON
                  const codeBlockMatch = msg.content.match(/```(?:update|json)?\s*\n?([\s\S]*?)```/);
                  if (codeBlockMatch) {
                    try {
                      const jsonContent = codeBlockMatch[1].trim();
                      // Check if it looks like JSON (starts with { or [)
                      if (jsonContent.startsWith('{') || jsonContent.startsWith('[')) {
                        updateData = JSON.parse(jsonContent);
                        // Remove all code blocks from display content
                        displayContent = msg.content.replace(/```(?:update|json)?\s*\n?[\s\S]*?```/g, '').trim();
                      }
                    } catch (e) {
                      console.error('Failed to parse code block as JSON:', e);
                    }
                  }
                  
                  // Skip the complex regex patterns and go straight to brace-balancing extraction
                  // This handles multi-line JSON better
                  
                  // Primary approach: Look for any JSON-like structure using brace balancing
                  if (!updateData && msg.content.includes('{') && msg.content.includes('"')) {
                    try {
                      // Find the start of JSON (first { character)
                      const jsonStart = msg.content.indexOf('{');
                      if (jsonStart !== -1) {
                        let braceCount = 0;
                        let bracketCount = 0;
                        let jsonEnd = jsonStart;
                        let inString = false;
                        let prevChar = '';
                        
                        // Use proper JSON parsing by tracking string boundaries
                        for (let i = jsonStart; i < msg.content.length; i++) {
                          const char = msg.content[i];
                          
                          // Track if we're inside a string
                          if (char === '"' && prevChar !== '\\') {
                            inString = !inString;
                          }
                          
                          // Only count braces outside of strings
                          if (!inString) {
                            if (char === '{') braceCount++;
                            if (char === '}') braceCount--;
                            if (char === '[') bracketCount++;
                            if (char === ']') bracketCount--;
                          }
                          
                          prevChar = char;
                          
                          // Check if we've closed all braces and brackets
                          if (braceCount === 0 && bracketCount === 0 && i > jsonStart) {
                            jsonEnd = i + 1;
                            break;
                          }
                        }
                        
                        if (jsonEnd > jsonStart) {
                          const jsonStr = msg.content.substring(jsonStart, jsonEnd);
                          // Only try to parse if it looks like valid JSON with expected keys
                          if (jsonStr.includes('"name"') || jsonStr.includes('"description"') || jsonStr.includes('"requirements"') || jsonStr.includes('"capabilities"')) {
                            updateData = JSON.parse(jsonStr);
                            displayContent = msg.content.substring(0, jsonStart).trim();
                            // Clean up common phrases before JSON
                            displayContent = displayContent
                              .replace(/Here's how the revised.*?:\s*$/i, '')
                              .replace(/Here's the revised.*?:\s*$/i, '')
                              .replace(/Here are the.*?:\s*$/i, '')
                              .replace(/:\s*$/, '')
                              .trim();
                            
                            // If display content is empty, provide a default
                            if (!displayContent) {
                              displayContent = "I've prepared the changes for you.";
                            }
                          }
                        }
                      }
                    } catch (e) {
                      console.log('JSON extraction failed:', e.message);
                    }
                  }
                }
                
                return (
                  <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0 mt-1">
                        <Sparkles className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                      msg.role === 'user' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm'
                    }`}>
                      {msg.role === 'assistant' ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 [&>h1]:text-base [&>h2]:text-sm [&>h3]:text-sm">
                          <ReactMarkdown>{displayContent}</ReactMarkdown>
                        </div>
                      ) : (
                        <div className="whitespace-pre-wrap">{displayContent}</div>
                      )}
                      {updateData && (
                        <div className="mt-3 space-y-2">
                          {/* Show preview of what will be applied */}
                          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                            <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-2 uppercase tracking-wide">✨ Proposed Changes</p>
                            <div className="text-sm text-gray-700 dark:text-gray-300 max-h-48 overflow-y-auto space-y-3">
                              {Object.entries(updateData).map(([key, value]) => {
                                // Format the key nicely
                                const formattedKey = key
                                  .replace(/([A-Z])/g, ' $1')
                                  .replace(/_/g, ' ')
                                  .replace(/^./, str => str.toUpperCase())
                                  .trim();
                                
                                return (
                                  <div key={key} className="pb-2 border-b border-gray-200 dark:border-gray-600 last:border-0 last:pb-0">
                                    <p className="font-semibold text-gray-800 dark:text-gray-200 text-xs uppercase tracking-wide mb-1">
                                      {formattedKey}
                                    </p>
                                    {Array.isArray(value) ? (
                                      <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                                        {value.map((item, idx) => (
                                          <li key={idx} className="text-sm leading-relaxed">
                                            {typeof item === 'string' 
                                              ? (item.length > 100 ? item.substring(0, 100) + '...' : item)
                                              : JSON.stringify(item)}
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                                        {typeof value === 'string' 
                                          ? (value.length > 200 ? value.substring(0, 200) + '...' : value)
                                          : JSON.stringify(value)}
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            className="w-full bg-green-600 hover:bg-green-700 gap-2"
                            onClick={() => onApplyUpdate(updateData)}
                          >
                            <Check className="w-4 h-4" /> Apply These Changes
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {copilotLoading && (
                <div className="flex gap-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0">
                    <Loader2 className="w-3 h-3 text-white animate-spin" />
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 shadow-sm">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            
            {/* Input */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col gap-2">
                <Textarea
                  value={copilotInput}
                  onChange={(e) => setCopilotInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onCopilotSend();
                    }
                  }}
                  placeholder="Ask for help... (Shift+Enter for new line)"
                  rows={3}
                  className="text-sm bg-white dark:bg-gray-800 resize-none"
                  disabled={copilotLoading}
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={onCopilotSend}
                    disabled={!copilotInput.trim() || copilotLoading}
                    className="bg-blue-600 hover:bg-blue-700 gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Send
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div>
            {canDelete && !deleteConfirm && (
              <Button
                variant="ghost"
                onClick={() => setDeleteConfirm(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
            {canDelete && deleteConfirm && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-red-600">Delete this item?</span>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={onDelete}
                >
                  Yes, Delete
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleteConfirm(false)}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={onSave} className="bg-blue-600 hover:bg-blue-700">
              {isNewItem ? 'Add' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditModal;
